/**
 * Impact: WHAT an incident (or a change) touches, as structured rows rather
 * than prose. "Live bidding latency" is a title; "TENANT lucky-d-ranch,
 * AUCTION fall-production-sale, RING main, SERVICE BID_ENGINE" is an impact
 * model, and it is what lets the platform later compute exposure, escalate
 * severity when an auction goes live, and tell one tenant instead of everyone.
 *
 * Resource ids are TEXT on purpose: a tenant is a slug, a service is a
 * component id, a deployment is a SHA. The label is captured at the time so
 * the record still reads correctly after a tenant is renamed.
 */

export const IMPACT_RESOURCE_TYPES = ['TENANT', 'AUCTION', 'RING', 'LOT', 'SERVICE', 'INTEGRATION', 'REGION', 'USER', 'DEPLOYMENT'] as const;
export type ImpactResourceType = (typeof IMPACT_RESOURCE_TYPES)[number];

export const IMPACT_TYPES = ['AFFECTED', 'DEGRADED', 'UNAVAILABLE', 'AT_RISK'] as const;
export type ImpactType = (typeof IMPACT_TYPES)[number];

/**
 * Criticality tiers (design doc section 89). They affect alerting, severity,
 * automation and change risk later; in Phase 1 they are a label on the
 * impact picker so an operator sees what they are naming.
 */
export const SERVICE_TIERS = ['TIER_0', 'TIER_1', 'TIER_2', 'TIER_3'] as const;
export type ServiceTier = (typeof SERVICE_TIERS)[number];

export const SERVICE_TIER_MEANING: Readonly<Record<ServiceTier, string>> = {
  TIER_0: 'Auction integrity and live bidding',
  TIER_1: 'Required transaction flows',
  TIER_2: 'Important but recoverable workflows',
  TIER_3: 'Optional functions',
};

export interface ServiceComponent {
  readonly id: string;
  readonly title: string;
  readonly tier: ServiceTier;
}

/**
 * The INTERNAL component registry. Public status components (Live Bidding,
 * Auction Websites, ...) and the internal -> public mapping are Phase 2, with
 * status.hipnum.com. This initial classification is a starting point and is
 * expected to be argued with; changing a tier is a row edit.
 */
export const SERVICE_COMPONENTS: readonly ServiceComponent[] = [
  { id: 'AUCTION_CORE', title: 'Auction core (reducer and event log)', tier: 'TIER_0' },
  { id: 'BID_ENGINE', title: 'Ring engine (live bidding)', tier: 'TIER_0' },
  { id: 'EVENT_STORE', title: 'Ring event store', tier: 'TIER_0' },
  { id: 'DATABASE', title: 'Postgres', tier: 'TIER_0' },
  { id: 'CONTROL_API', title: 'Control API', tier: 'TIER_1' },
  { id: 'AUTH', title: 'Authentication (Cognito, sessions)', tier: 'TIER_1' },
  { id: 'PAYMENTS', title: 'Payments and invoicing', tier: 'TIER_1' },
  { id: 'STOREFRONT', title: 'Tenant storefronts', tier: 'TIER_1' },
  { id: 'SETTLEMENT', title: 'Settlement projector and statements', tier: 'TIER_1' },
  { id: 'ADMIN', title: 'Admin and console', tier: 'TIER_2' },
  { id: 'SMS', title: 'SMS and notifications', tier: 'TIER_2' },
  { id: 'ANNIE', title: 'Annie', tier: 'TIER_2' },
  { id: 'MEDIA', title: 'Media storage and processing', tier: 'TIER_2' },
  { id: 'GFX', title: 'Broadcast graphics', tier: 'TIER_3' },
  { id: 'MARKETING', title: 'Marketing Studio', tier: 'TIER_3' },
  { id: 'DISPLAY', title: 'Arena displays', tier: 'TIER_3' },
];

export const serviceComponent = (id: string): ServiceComponent | undefined => SERVICE_COMPONENTS.find((c) => c.id === id);

/** Third parties an incident can name as the thing that broke. */
export const INTEGRATIONS = ['AWS', 'STRIPE', 'TWILIO', 'AWS_END_USER_MESSAGING', 'META', 'YOUTUBE', 'GOOGLE', 'RESEND', 'CLOUDFLARE', 'GITHUB', 'XAI'] as const;
export type Integration = (typeof INTEGRATIONS)[number];
